import * as Popover from '@radix-ui/react-popover';
import { useRef } from 'react';
import { BottomSheet } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { dayLabel } from './calendarDates';

export interface FreeDayTarget {
  propertyId: string;
  propertyName: string;
  date: string;
  /** Die angeklickte Zelle: Anker des Popovers und Ziel des Fokus beim Schließen. */
  anchor: HTMLElement;
}

interface Props {
  target: FreeDayTarget | null;
  mobile: boolean;
  onClose: () => void;
  onCreate: (target: FreeDayTarget) => void;
}

const HINT = 'Zeitraum, Gast und Preis legst du im nächsten Schritt fest.';

/**
 * „Freier Tag“ (Board „Belegung“): ein Zwischenschritt vor der manuellen Buchung, damit ein
 * versehentlicher Klick ins Raster nicht gleich das Formular öffnet. Am Handy ein Blatt von unten.
 */
export function FreeDayPrompt({ target, mobile, onClose, onCreate }: Props) {
  const lastAnchor = useRef<HTMLElement | null>(null);
  if (target) lastAnchor.current = target.anchor;
  const title = target ? `${target.propertyName} · ab ${dayLabel(target.date)}` : '';
  const create = () => {
    if (!target) return;
    // Der Fokus geht zurück an den Tag, bevor das Formular aufgeht – dorthin kehrt er nach dem
    // Schließen zurück, statt auf einem schon entfernten Knopf zu landen.
    target.anchor.focus();
    onCreate(target);
    onClose();
  };
  const createButton = (size: string) => (
    <button
      type="button"
      onClick={create}
      className={cn(
        'rounded-pill bg-forest flex items-center justify-center whitespace-nowrap px-4 font-bold text-white',
        size,
      )}
    >
      Manuelle Buchung anlegen
    </button>
  );
  if (mobile) {
    return (
      <BottomSheet
        open={target !== null}
        onOpenChange={(open) => !open && onClose()}
        title={title}
        description="Freier Tag"
        footer={createButton('h-[52px] w-full text-[15px]')}
      >
        <p className="text-ink-soft text-sm">{HINT}</p>
      </BottomSheet>
    );
  }
  return (
    <Popover.Root open={target !== null} onOpenChange={(open) => !open && onClose()}>
      {target && <Popover.Anchor virtualRef={{ current: target.anchor }} />}
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={6}
          collisionPadding={16}
          aria-label="Neue Buchung"
          // Ein Klick auf einen anderen freien Tag verschiebt das Popover, statt es nur zu schließen.
          onInteractOutside={(event) => {
            if ((event.target as HTMLElement | null)?.closest?.('[data-free-day]')) {
              event.preventDefault();
            }
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            lastAnchor.current?.focus();
          }}
          className="bg-card border-hairline shadow-panel rounded-tile z-50 flex w-[340px] max-w-[calc(100vw-32px)] flex-col gap-3 border p-[18px]"
        >
          <span className="text-forest font-mono text-[10px] font-semibold uppercase tracking-[0.16em]">
            Freier Tag
          </span>
          <span className="font-serif text-lg font-bold">{title}</span>
          <span className="text-ink-soft text-[13px]">{HINT}</span>
          <div className="flex gap-2">
            {createButton('h-11 text-[13px]')}
            <Popover.Close asChild>
              <button
                type="button"
                className="rounded-pill bg-card-alt h-11 px-3.5 text-[13px] font-bold"
              >
                Abbrechen
              </button>
            </Popover.Close>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
